// General → Council: how Council mode behaves (user_settings.council_settings), plus a button to open it.
// The server reads replyMode / maxSpeakers / syqiChairs when it picks who answers (src/lib/agents/council.ts).
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { DEFAULT_COUNCIL, withCouncilDefaults, type CouncilSettings } from '../../council/councilApi';
import { openCouncil } from '../../live/appActions';
import { GEAR_TIERS, TIER_ORDER } from '../ascension';
import { GEAR_ORDER, SEAT_BY_ID } from '../../v10/roster';
import { makeStyles, useV10 } from '../../v10/theme';
import { ThemedSwitch } from './ThemedSwitch';
import { useSettingColumn } from './useSettingColumn';

const ALL_SEATS = ['syqi', ...GEAR_ORDER.flatMap((g) => TIER_ORDER.map((tier) => GEAR_TIERS[g][tier]))];
const nameOf = (id: string) => (id === 'syqi' ? 'SyQi' : SEAT_BY_ID[id]?.name ?? id.toUpperCase());

export function CouncilEditor({ userId }: { userId: string | undefined }) {
  const t = useV10();
  const styles = useStyles();
  const col = useSettingColumn<Partial<CouncilSettings> | null>(userId, 'council_settings', null);
  const s = withCouncilDefaults(col.value);
  const set = (patch: Partial<CouncilSettings>) => col.setValue({ ...s, ...patch });
  if (col.loading) return <Text style={styles.hint}>Loading…</Text>;

  const row = (label: string, hint: string, value: boolean, onChange: (v: boolean) => void) => (
    <View style={styles.row}>
      <View style={{ flex: 1, gap: 2 }}>
        <Text style={styles.label}>{label}</Text>
        <Text style={styles.hint}>{hint}</Text>
      </View>
      <ThemedSwitch value={value} onValueChange={onChange} />
    </View>
  );
  const chip = (label: string, on: boolean, onPress: () => void) => (
    <Pressable key={label} onPress={onPress} style={[styles.chip, on && styles.chipOn]}>
      <Text style={[styles.chipText, on && { color: t.onGold }]}>{label}</Text>
    </Pressable>
  );

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <Pressable onPress={() => openCouncil({})} style={styles.open} accessibilityLabel="Open the Council">
        <Text style={styles.openText}>Open the Council</Text>
      </Pressable>
      <Text style={styles.hint}>Or say "council mode" to SyQi. Each council keeps its own session history; your one-on-one chats with each deity stay in that deity's own record.</Text>

      {row('Conference', 'Read every reply aloud in each deity\'s voice as it arrives.', s.autoSpeak, (v) => set({ autoSpeak: v }))}
      {row('Hands-free mic', 'The mic starts listening when the room opens. It pauses while deities speak.', s.voiceInput, (v) => set({ voiceInput: v }))}
      {row('SyQi chairs', 'When two or more deities answer, SyQi closes the round with the decision and who owns it.', s.syqiChairs, (v) => set({ syqiChairs: v }))}

      <Text style={styles.section}>Who answers</Text>
      <View style={styles.chips}>
        {chip('Everyone seated', s.replyMode === 'all', () => set({ replyMode: 'all' }))}
        {chip('Only who I name', s.replyMode === 'addressed', () => set({ replyMode: 'addressed' }))}
      </View>
      <Text style={styles.hint}>Naming a deity ("KRNOS, when am I free?") always goes straight to them.</Text>

      <Text style={styles.section}>Most deities per round</Text>
      <View style={styles.chips}>{[1, 2, 3, 4, 5, 6].map((n) => chip(String(n), s.maxSpeakers === n, () => set({ maxSpeakers: n })))}</View>

      <Text style={styles.section}>Seated by default</Text>
      <View style={styles.chips}>
        {ALL_SEATS.map((id) =>
          chip(nameOf(id), s.defaultMembers.includes(id), () =>
            set({ defaultMembers: s.defaultMembers.includes(id) ? s.defaultMembers.filter((x) => x !== id) : [...s.defaultMembers, id] }),
          ),
        )}
      </View>
      <Pressable onPress={() => col.setValue({ ...DEFAULT_COUNCIL })} style={styles.reset}>
        <Text style={styles.hint}>Reset Council settings</Text>
      </Pressable>
    </ScrollView>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    content: { padding: 16, gap: 12, paddingBottom: 40 },
    row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 6 },
    label: { color: t.ivory, fontSize: 15, fontWeight: '700' },
    hint: { color: t.ivoryFaint, fontSize: 12, lineHeight: 17 },
    section: { color: t.gold, fontSize: 12, fontWeight: '800', letterSpacing: 1.6, textTransform: 'uppercase', marginTop: 8 },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1, borderColor: t.gold },
    chipOn: { backgroundColor: t.gold },
    chipText: { color: t.ivory, fontSize: 13, fontWeight: '700' },
    open: { height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', backgroundColor: t.gold },
    openText: { color: t.onGold, fontSize: 15, fontWeight: '800', letterSpacing: 1 },
    reset: { alignSelf: 'center', paddingVertical: 10 },
  }),
);
